import React, { useEffect, useRef, useState } from 'react';
import cl from 'classnames';
import { useContextData, useLanguage } from 'src/hooks';
import { Select } from 'src/components/general';
import styles from './overlapSwitcherStyle.module.scss';

type OverlapSwitcherProps = {
  currentId: string | number;
  items: { value: string | number; label: string }[];
  selectItem: (value: string | number) => void;
  className?: string;
  selectClassName?: string;
};

type Element = {
  label: string;
  left: number;
  width: number;
};

type Position = 'start' | 'center' | 'end';

export const OverlapSwitcher = ({
  currentId,
  items,
  selectItem,
  className,
  selectClassName,
}: OverlapSwitcherProps) => {
  const { currentLang } = useLanguage();
  const {
    screen: { isLaptop },
  } = useContextData();
  const [element, setElement] = useState<Element | null>(null);
  const refs = useRef<
    Record<
      string | number,
      { node: HTMLButtonElement; position: Position; label: string }
    >
  >({});

  useEffect(() => {
    const { node, position, label } = refs.current[currentId];
    let left = node.offsetLeft - 1;
    let width = node.getBoundingClientRect().width + 10;

    if (position !== 'start') {
      left -= 8;
    }

    if (position === 'center') {
      width += 8;
    }

    setElement(
      isLaptop
        ? null
        : {
            label,
            left,
            width,
          },
    );
  }, [currentId, isLaptop, currentLang]);

  return (
    <div className={cl(styles.container, className)}>
      <div className={styles.buttons}>
        {items.map(({ value, label }, index) => (
          <button
            ref={(node) => {
              if (node) {
                let position: Position = 'start';

                if (index > 0) {
                  position = index === items.length - 1 ? 'end' : 'center';
                }

                refs.current[value] = {
                  node,
                  position,
                  label,
                };
              }
            }}
            key={`swBtn-${value}`}
            className={cl(styles.button)}
            type="button"
            onClick={() => {
              selectItem(value);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {element && (
        <div
          className={cl(styles.button, styles.current)}
          style={{
            left: element.left,
            width: element.width,
          }}
        >
          {element.label}
        </div>
      )}
      <div className={styles.mobileGroup}>
        <Select
          value={currentId}
          items={items}
          selectItem={(value) => {
            selectItem(value as string | number);
          }}
          design="switcherBorder"
          className={selectClassName}
        />
      </div>
    </div>
  );
};

export default OverlapSwitcher;
